// ButtonControl.tsx
import React, { lazy, useState, Suspense, useEffect, useCallback } from 'react';
import { Control } from '../ControlRenderer';
import { get, set, evaluateTemplate } from '../utils/Utils'
import { Button, IconButton, Tooltip, CircularProgress, Link, MenuItem, Typography } from '@mui/material';
import { FormModeType, useFormData } from '@/components/FormDataProvider';
import IconMap from '../utils/IconMap';
import ModalBox, { Action } from '../ModalBox';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { useConfig } from '@/components/ConfigProvider';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/components/AuthProvider';
import { useValidation } from '@/components/ValidationProvider';
import useActionHandler from '@/hooks/useActionHandler';

/**
* Represents a button control with specific properties for actions, icons, and styling.
* Extends the base Control interface.
*/
export interface ButtonControl extends Control {
  /**
   * The action to be executed when the button is clicked.
   */
  clickAction: Action;

  /**
   * A string representing the icon to be displayed on the button.
   * This could refer to a CSS class or an icon identifier. (Optional)
   */
  icon?: string;

  /**
   * Defines the font size of the button's label.
   * Allows customization for design and readability. (Optional)
   */
  fontSize?: number;

  /**
   * Disable the button if the data context has 
   * validation errors. (Optional)
   */
  disableIfInvalid?: boolean;

  /**
   * The key that triggers the button action when pressed.
   * This is useful for form submission via keyboard shortcuts,
   * especially when the button is intended to handle form-related 
   */
  formActionKey?: string;

  /**  * Whether to hide the tooltip for the button.
   */
  hideTooltip?: boolean;
  /**
   * Enable or disable text wrapping for the button label.
   * Default is false.
   */
  wrapText?: boolean;
}

interface ButtonControlProps {
  control: Control;
  data: any;
  mode: FormModeType;
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
  sx?: SxProps<Theme>;
}

const ButtonControl: React.FC<ButtonControlProps> = ({ control, data, mode, onComplete, onError, onClose, sx }) => {
  const { errors } = useValidation();
  const { executeAction, UIAction, handleSuccess, handleFailure, handleClose } = useActionHandler({ onComplete: onComplete, onError: onError, onClose: onClose });

  let currentMode = control.mode ? control.mode : mode;

  const buttonControl = control as ButtonControl;

  const isInvalid = buttonControl.disableIfInvalid && Object.keys(errors).length > 0;

  const wrapText = buttonControl.wrapText ?? false;

  const handleClick = useCallback(() => {
    executeAction(buttonControl.clickAction, data);
  }, [executeAction, buttonControl.clickAction, data]);

  useEffect(() => {
    // Only add the event listener if formActionKey is present
    if (!buttonControl.formActionKey) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === buttonControl.formActionKey) {
        console.log(`Key '${event.key}' pressed on form`);
        handleClick();
      }
    };

    document.addEventListener('keypress', handleKeyPress);

    // Cleanup
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [buttonControl.formActionKey, handleClick]);

  const icon = buttonControl.icon;
  const IconComponent = icon ? IconMap[icon] : null;
  const hideTooltip = buttonControl.hideTooltip ?? false;

  const buttonStyles: SxProps<Theme> = {
    fontSize: buttonControl.fontSize,
    whiteSpace: wrapText ? 'normal' : 'nowrap', // Enable or disable text wrapping
    overflow: 'hidden', // Ensure hidden overflow
    ...sx
  };

  return (
    <>
      {control.type === 'link' ? (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          disabled={currentMode === 'view'}
          className={`eform-${control.type}`}
          onClick={handleClick}
          sx={buttonStyles}
        >
          {control.label}
        </Link>
      ) : IconComponent ? (
        <Tooltip title={hideTooltip ? "" : control.label}>
          <span>
            <Suspense fallback={<CircularProgress size={24} />}>
              <IconButton
                onClick={handleClick}
                disabled={currentMode === 'view' || isInvalid}
                className={`eform-${control.type}`}
                sx={sx}
              >
                <IconComponent />
              </IconButton>
            </Suspense>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant={control.type === 'gridbutton' ? 'outlined' : 'contained'}
          className={`eform-${control.type}`}
          onClick={handleClick}
          disabled={currentMode === 'view' || isInvalid}
          sx={buttonStyles}
        >
          {control.label}
        </Button>
      )}
      {UIAction &&
        <ModalBox
          action={UIAction}
          data={data}
          onDataChange={() => { }}
          mode={mode}
          onComplete={() => handleSuccess(UIAction, data)}
          onError={() => handleFailure(UIAction, data)}
          onClose={() => handleClose(UIAction, data)} />}
    </>
  );
};

export default ButtonControl;